import { z } from 'zod';
/**
 * Who performed the action: an end user proxied through primary, a service account,
 * or a proxied caller with no user id (an unauthenticated request).
 */
export const ResourceGatewayActorKindSchema = z.enum(['user', 'service', 'anonymous']);
/** The result of the audited action. */
export const ResourceGatewayOutcomeSchema = z.enum(['allowed', 'denied', 'error']);
/**
 * The caller's organization memberships. Absent means global organization access
 * (see resource-gateway's `hasOrganizationAccess`), so it is never defaulted to `[]`.
 */
const ResourceGatewayActorOrganizationIdsSchema = z.array(z.uuid()).optional();
export const ResourceGatewayActorSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('user'),
        id: z.string(),
        organizationIds: ResourceGatewayActorOrganizationIdsSchema,
    }),
    // Not `.uuid()`: service accounts are identified by a name.
    z.object({ kind: z.literal('service'), id: z.string() }),
    z.object({
        kind: z.literal('anonymous'),
        organizationIds: ResourceGatewayActorOrganizationIdsSchema,
    }),
]);
export const ResourceGatewayBundleEntryMethodSchema = z.enum(['GET', 'POST', 'PUT', 'DELETE']);
/**
 * The thing the action was performed on. A discriminated union on `kind` so it
 * stays type-safe as new target shapes are added (e.g. a connection, a feed).
 */
export const ResourceGatewayTargetSchema = z.discriminatedUnion('kind', [
    // `resourceId` is absent for type-level requests such as a search or a failed create.
    z.object({
        kind: z.literal('resource'),
        resourceKey: z.string(),
        resourceId: z.string().optional(),
    }),
    z.object({ kind: z.literal('organization'), id: z.string() }),
    // A bundle entry whose URL names no FHIR resource type; the URL itself can carry PHI, so it is never stored.
    z.object({ kind: z.literal('unattributed') }),
]);
/** Which entry of a FHIR bundle an event describes; a bundle request emits one event per entry. */
export const ResourceGatewayAuditBundleEntrySchema = z.object({
    bundleType: z.enum(['batch', 'transaction']),
    index: z.int().nonnegative(),
    entryCount: z.int().positive(),
    method: ResourceGatewayBundleEntryMethodSchema,
    /** The entry's own response status; absent when the bundle failed as a whole. */
    status: z.int().optional(),
});
/** HTTP context for events emitted at an HTTP boundary. */
export const ResourceGatewayAuditRequestSchema = z.object({
    method: z.string(),
    /** Route path with UUIDs replaced; never the raw URL, which can carry PHI in its query string. */
    path: z.string(),
    statusCode: z.int(),
    /** Client IP as reported by primary in the proxied user header. */
    ip: z.string().optional(),
    userAgent: z.string().optional(),
    /** Address of the peer that connected to resource-gateway. */
    sourceIp: z.string().optional(),
    bundleEntry: ResourceGatewayAuditBundleEntrySchema.optional(),
});
/**
 * A single audit event as supplied by a call site. The server stamps the id and
 * timestamp on write — callers never provide them.
 */
export const ResourceGatewayAuditEventSchema = z.object({
    actor: ResourceGatewayActorSchema,
    /** Dotted action name, e.g. `resource.read`, `organization.purge`. */
    action: z.string().min(1),
    /** Absent when the request was refused before an organization was resolved. */
    organizationId: z.uuid().optional(),
    target: ResourceGatewayTargetSchema,
    outcome: ResourceGatewayOutcomeSchema,
    request: ResourceGatewayAuditRequestSchema.optional(),
    requestId: z.string().optional(),
    correlationId: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
//# sourceMappingURL=auditEvent.js.map